import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import {
  FirmwareImage,
  FirmwareListResponse,
  FirmwareUploadURLResponse,
  FirmwareDownloadURLResponse,
  FirmwarePublishOptions,
} from '../types';

/**
 * Byte offset in the STM32WB50 firmware .bin where FW_VERSION (uint16, LE)
 * lives — the first location after the interrupt vector table. Matches
 * Matt's C macro:
 *
 *   #define FW_VERSION   (*(uint16_t *)&fw_data[0x13c])
 *
 * We parse this client-side in the upload flow so the user never types the
 * version manually; the server re-reads the same offset at finalize time
 * and rejects the upload if the two disagree.
 */
export const FIRMWARE_VERSION_OFFSET = 0x13c;

export interface DetectedFirmwareVersion {
  /** The raw uint16 value at offset 0x13C, or null if the file is too short / zero. */
  version: number | null;
  /** Human-readable formatted version (e.g., "0.06") — empty string if version is null. */
  formatted: string;
  /** Error message if parsing failed. */
  error?: string;
}

/**
 * Format a HOTA integer version as "{N/100}.{N%100}" with two-digit minor.
 * Mirrors David's convention in Matt's firmware: 5 → "0.05", 500 → "5.00".
 */
export function formatFirmwareVersion(version: number): string {
  const major = Math.floor(version / 100);
  const minor = version % 100;
  return `${major}.${minor.toString().padStart(2, '0')}`;
}

/**
 * Read the embedded firmware version from a File client-side, so the upload
 * modal can display it read-only without the user typing anything.
 *
 * We slice to just the bytes we need (up to offset+2) rather than reading
 * the whole file — tiny perf win, but also means a 2GB file picked by
 * mistake won't try to allocate a 2GB ArrayBuffer before erroring out.
 */
export async function detectFirmwareVersion(file: File): Promise<DetectedFirmwareVersion> {
  const needed = FIRMWARE_VERSION_OFFSET + 2;
  if (file.size < needed) {
    return {
      version: null,
      formatted: '',
      error: `file is too short (${file.size} bytes) to contain a firmware version at offset 0x${FIRMWARE_VERSION_OFFSET.toString(16).toUpperCase()}`,
    };
  }
  const slice = file.slice(FIRMWARE_VERSION_OFFSET, needed);
  const buf = await slice.arrayBuffer();
  const view = new DataView(buf);
  // little-endian uint16 — matches Matt's `(uint16_t *)&fw_data[0x13c]` on
  // the STM32WB50 (ARM, little-endian).
  const version = view.getUint16(0, true);
  if (version === 0) {
    return {
      version: null,
      formatted: '',
      error: 'embedded firmware version is zero — not a valid build',
    };
  }
  return { version, formatted: formatFirmwareVersion(version) };
}

/**
 * FirmwareActionError is thrown by the action helpers (publish, unpublish,
 * rollback, purgeBroker, deleteFirmware) for any axios failure, carrying the
 * HTTP status and the firmware id the action targeted.
 *
 * The 404 case is the one the UI cares about most: a row the user sees in
 * their list can be out-of-date if another session deleted it, and we want
 * to auto-refresh rather than leave the user staring at a ghost row. Use
 * isFirmwareGoneError to detect that case.
 */
export class FirmwareActionError extends Error {
  readonly status: number;
  readonly id: number;

  constructor(message: string, status: number, id: number) {
    super(message);
    this.name = 'FirmwareActionError';
    this.status = status;
    this.id = id;
  }
}

/**
 * isFirmwareGoneError returns true when an action helper failed because the
 * target row no longer exists on the server (HTTP 404). Callers should treat
 * this as "refresh the list, the row is gone" rather than a retryable error.
 */
export function isFirmwareGoneError(err: unknown): err is FirmwareActionError {
  return err instanceof FirmwareActionError && err.status === 404;
}

/**
 * axiosStatus returns the HTTP status for an axios-shaped error, or 0 if
 * the error didn't come from axios / didn't have a response.
 */
function axiosStatus(err: unknown): number {
  if (axios.isAxiosError(err)) return err.response?.status ?? 0;
  return 0;
}

export interface UploadProgress {
  phase: 'idle' | 'requesting-url' | 'uploading' | 'finalizing' | 'done' | 'error';
  percent: number;
  message?: string;
  error?: string;
}

export interface UploadRequest {
  file: File;
  deviceType: string;
  notes: string;
}

export function useFirmware() {
  const [images, setImages] = useState<FirmwareImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axiosInstance.get<FirmwareListResponse>(`${config.firmwareApiUrl}`);
      setImages(data.images ?? []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load firmware list';
      setError(message);
      setImages([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { images, loading, error, refresh };
}

/**
 * uploadFirmware runs the full 3-step upload flow:
 *   1. Parse the embedded version from the .bin at offset 0x13C
 *   2. POST /firmware/upload-url  → get a presigned S3 PUT URL + new row id
 *   3. PUT the file bytes directly to S3
 *   4. POST /firmware/{id}/upload-complete → Lambda re-reads offset 0x13C
 *      from the uploaded bytes, verifies it matches the pending row, and
 *      finalizes with hash + size.
 *
 * Version is never entered manually — it comes from the firmware file
 * itself. The client parses it here so the upload-url call can populate
 * the pending row; the server re-reads the same offset at finalize time
 * and rejects on mismatch (defense in depth against a client bug).
 *
 * Progress updates fire via the onProgress callback.
 */
export async function uploadFirmware(
  req: UploadRequest,
  onProgress: (p: UploadProgress) => void,
): Promise<FirmwareImage> {
  // Step 0: parse the embedded version from the .bin.
  onProgress({ phase: 'requesting-url', percent: 0, message: 'Reading firmware version…' });
  const detected = await detectFirmwareVersion(req.file);
  if (detected.version === null) {
    const message = detected.error ?? 'could not detect firmware version';
    onProgress({ phase: 'error', percent: 0, error: message });
    throw new Error(message);
  }

  onProgress({ phase: 'requesting-url', percent: 0, message: 'Requesting upload URL…' });

  let urlResp;
  try {
    const { data } = await axiosInstance.post<FirmwareUploadURLResponse>(
      `${config.firmwareApiUrl}/upload-url`,
      {
        version: detected.version,
        device_type: req.deviceType,
        original_filename: req.file.name,
        notes: req.notes,
      },
    );
    urlResp = data;
  } catch (err) {
    const message = extractApiError(err, 'Failed to request upload URL');
    onProgress({ phase: 'error', percent: 0, error: message });
    throw new Error(message);
  }

  // Step 2: PUT the file to S3. Use a plain axios (not axiosInstance) so the
  // Authorization: Bearer header isn't sent to S3 — S3 would reject it.
  onProgress({ phase: 'uploading', percent: 0, message: 'Uploading to S3…' });
  try {
    await axios.put(urlResp.presigned_url, req.file, {
      headers: { 'Content-Type': urlResp.content_type },
      onUploadProgress: (evt) => {
        if (evt.total) {
          const pct = Math.round((evt.loaded / evt.total) * 100);
          onProgress({ phase: 'uploading', percent: pct, message: `Uploading… ${pct}%` });
        }
      },
    });
  } catch (err) {
    const message = extractApiError(err, 'Failed to upload to S3');
    onProgress({ phase: 'error', percent: 0, error: message });
    throw new Error(message);
  }

  // Step 3: finalize the upload — Lambda hashes the S3 object and writes metadata.
  onProgress({ phase: 'finalizing', percent: 100, message: 'Finalizing upload…' });
  let finalized: FirmwareImage;
  try {
    const { data } = await axiosInstance.post<FirmwareImage>(
      `${config.firmwareApiUrl}/${urlResp.id}/upload-complete`,
      {},
    );
    finalized = data;
  } catch (err) {
    const message = extractApiError(err, 'Failed to finalize upload');
    onProgress({ phase: 'error', percent: 100, error: message });
    throw new Error(message);
  }

  onProgress({ phase: 'done', percent: 100, message: 'Upload complete' });
  return finalized;
}

/**
 * Fetch a presigned download URL for a firmware row. The caller can open it
 * in a new tab or feed it to window.location for a browser-native download.
 */
export async function getDownloadURL(id: number): Promise<string> {
  const { data } = await axiosInstance.get<FirmwareDownloadURLResponse>(
    `${config.firmwareApiUrl}/${id}/download-url`,
  );
  return data.presigned_url;
}

// ---------------------------------------------------------------------------
// Publish / Unpublish / Rollback / Test Download
// ---------------------------------------------------------------------------

export interface PublishResponse {
  firmware: FirmwareImage;
  blocks: number;
  block_size: number;
  image_size: number;
  start_topic: string;
  crc32_hex: string;
  /**
   * Present only when the publish was targeted at a single device_id
   * (Matt's 2026-04-11 addition). Omitted for fleet-wide publishes so the
   * response shape for global publishes is unchanged.
   */
  device_id?: string;
  scheme: {
    device_type: string;
    start_topic: string;
    description: string;
    production: boolean;
  };
}

export interface TestDownloadBlock {
  block: number;
  topic: string;
  ok: boolean;
  bytes?: number;
  error?: string;
}

// ---------------------------------------------------------------------------
// Broker state / purge broker
// ---------------------------------------------------------------------------

export interface BrokerBlockState {
  block: number;
  topic: string;
  bytes: number;
}

export interface BrokerStateResponse {
  device_type: string;
  version: number;
  scheme_start_topic: string;
  block_topic_template: string;
  start_record_present: boolean;
  start_record_topic: string;
  start_record_bytes: number;
  start_record_payload?: string;
  blocks_found: BrokerBlockState[];
  blocks_found_count: number;
  total_block_bytes: number;
  missing_within_scan: number[];
  highest_probed: number;
  stop_reason: string; // "end-of-chain" | "floor-empty" | "hard-cap"
}

export interface PurgeBrokerResponse {
  scan: BrokerStateResponse;
  cleared_start_record: boolean;
  cleared_blocks: BrokerBlockState[];
  cleared_count: number;
  total_bytes_cleared: number;
  errors: string[];
  firmware: FirmwareImage;
}

/** Read-only scan of the broker for this firmware's expected topics. */
export async function getBrokerState(id: number): Promise<BrokerStateResponse> {
  try {
    const { data } = await axiosInstance.get<BrokerStateResponse>(
      `${config.firmwareApiUrl}/${id}/broker-state`,
    );
    return data;
  } catch (err) {
    throw new FirmwareActionError(
      extractApiError(err, 'Failed to scan broker state'),
      axiosStatus(err),
      id,
    );
  }
}

/**
 * Broker scan keyed on device_type alone — no firmware row required.
 * The backend resolves the scheme's start topic (e.g. HOTA/X), reads
 * whatever retained start record is there, parses the advertised
 * fw_version, and scans the block chain for that version.
 *
 * If no start record is present, `start_record_present` is false and
 * `stop_reason` is "no-start-record"; the blocks list is empty.
 *
 * Extends BrokerStateResponse with the parsed start-record fields so
 * the UI can show what the current retained publish is advertising
 * (version, image size, optional device_id targeting) without having
 * to parse start_record_payload itself.
 */
export interface BrokerStateByDeviceTypeResponse extends BrokerStateResponse {
  start_record_fw_version?: number;
  start_record_image_size?: number;
  start_record_device_id?: string;
}

export async function getBrokerStateByDeviceType(
  deviceType: string,
): Promise<BrokerStateByDeviceTypeResponse> {
  try {
    const { data } = await axiosInstance.get<BrokerStateByDeviceTypeResponse>(
      `${config.firmwareApiUrl}/broker-state`,
      { params: { device_type: deviceType } },
    );
    return data;
  } catch (err) {
    throw new Error(extractApiError(err, 'Failed to scan broker state'));
  }
}

/** Clear every retained MQTT message for this firmware (start record + blocks). */
export async function purgeBroker(id: number): Promise<PurgeBrokerResponse> {
  try {
    const { data } = await axiosInstance.post<PurgeBrokerResponse>(
      `${config.firmwareApiUrl}/${id}/purge-broker`,
      {},
    );
    return data;
  } catch (err) {
    throw new FirmwareActionError(
      extractApiError(err, 'Failed to purge broker'),
      axiosStatus(err),
      id,
    );
  }
}

// ---------------------------------------------------------------------------
// Delete firmware (row + S3 + broker)
// ---------------------------------------------------------------------------

export interface DeleteFirmwareResponse {
  id: number;
  /** Present only when device_type is in the publish allowlist. */
  broker_cleanup?: PurgeBrokerResponse;
  s3_key: string;
  s3_object_deleted: boolean;
  s3_error?: string;
  db_row_deleted: boolean;
  db_error?: string;
}

/**
 * deleteFirmware tears down a firmware row completely:
 *   1. Clears retained broker messages (if the device_type is publishable)
 *   2. Deletes the S3 object
 *   3. Deletes the DB row
 *
 * The backend returns a per-step report regardless of outcome so the UI can
 * show which parts succeeded. DB deletion is the only required step — if it
 * fails the API returns 500 with a partial result body.
 */
export async function deleteFirmware(id: number): Promise<DeleteFirmwareResponse> {
  try {
    const { data } = await axiosInstance.delete<DeleteFirmwareResponse>(
      `${config.firmwareApiUrl}/${id}`,
    );
    return data;
  } catch (err) {
    // If the server returned a structured partial result (500 with body),
    // surface it via the error message so the modal can display the details.
    let message = extractApiError(err, 'Failed to delete firmware');
    if (axios.isAxiosError(err) && err.response?.data) {
      const data = err.response.data as Partial<DeleteFirmwareResponse> & { error?: string };
      if (data.error) message = data.error;
      else if (data.db_error) message = `DB delete failed: ${data.db_error}`;
    }
    throw new FirmwareActionError(message, axiosStatus(err), id);
  }
}

export interface TestDownloadResponse {
  firmware_id: number;
  device_type: string;
  version: number;
  start_topic: string;
  block_topic_template: string;
  expected_blocks: number;
  start_record_ok: boolean;
  start_record_error?: string;
  start_record_version?: number;
  start_record_image_size?: number;
  // Optional because the server's early-return paths (no retained start
  // record, unparseable start record) historically omitted the field.
  // 2026-04-11 server fix always populates an empty array, but the
  // client keeps the type optional so older deploys don't crash.
  blocks?: TestDownloadBlock[];
  all_blocks_ok: boolean;
  reassembled_matches_s3?: boolean;
  reassembled_bytes?: number;
  expected_bytes?: number;
  reassembly_verify_error?: string;
  // CRC verification fields populated when all blocks are present.
  stored_padded_crc32?: string;         // from firmware_images.last_crc32
  reassembled_padded_crc32?: string;    // CRC-32 of reassembled[:len-4]
  padded_crc32_matches_stored?: boolean;
  reassembled_full_crc32?: string;      // CRC-32 of full reassembled buffer — should be FFFFFFFF
  bootloader_magic_ok?: boolean;        // true iff reassembled_full_crc32 == FFFFFFFF
}

/**
 * publishFirmware chunks the stored binary and publishes blocks + start
 * record to the IoT broker.
 *
 * Options:
 *   - blockSize: optional override of the default 2048-byte block size
 *   - deviceId: optional single-device targeting. When set, the server
 *     includes a "device_id" field in the start record so only that
 *     device acts on the update. When omitted/empty, the publish is
 *     fleet-wide (current device contract).
 */
export async function publishFirmware(
  id: number,
  options: FirmwarePublishOptions = {},
): Promise<PublishResponse> {
  try {
    const body: Record<string, unknown> = {};
    if (options.blockSize) body.block_size = options.blockSize;
    if (options.deviceId && options.deviceId.trim() !== '') body.device_id = options.deviceId.trim();
    const { data } = await axiosInstance.post<PublishResponse>(
      `${config.firmwareApiUrl}/${id}/publish`,
      body,
    );
    return data;
  } catch (err) {
    throw new FirmwareActionError(
      extractApiError(err, 'Failed to publish firmware'),
      axiosStatus(err),
      id,
    );
  }
}

/** unpublishFirmware clears the retained start record (devices stop seeing it). */
export async function unpublishFirmware(id: number): Promise<FirmwareImage> {
  try {
    const { data } = await axiosInstance.post<{ firmware: FirmwareImage; cleared_topic: string }>(
      `${config.firmwareApiUrl}/${id}/unpublish`,
      {},
    );
    return data.firmware;
  } catch (err) {
    throw new FirmwareActionError(
      extractApiError(err, 'Failed to unpublish firmware'),
      axiosStatus(err),
      id,
    );
  }
}

/** rollbackFirmware re-publishes a prior version's start record (blocks assumed still retained). */
export async function rollbackFirmware(id: number): Promise<FirmwareImage> {
  try {
    const { data } = await axiosInstance.post<{ firmware: FirmwareImage; start_topic: string }>(
      `${config.firmwareApiUrl}/${id}/rollback`,
      {},
    );
    return data.firmware;
  } catch (err) {
    throw new FirmwareActionError(
      extractApiError(err, 'Failed to rollback firmware'),
      axiosStatus(err),
      id,
    );
  }
}

/**
 * testDownload fetches every retained message for a published firmware and
 * verifies it reassembles to match the stored S3 copy. Returns a per-block
 * pass/fail report — the UI renders it directly.
 */
export async function testDownload(id: number): Promise<TestDownloadResponse> {
  try {
    const { data } = await axiosInstance.get<TestDownloadResponse>(
      `${config.firmwareApiUrl}/${id}/test-download`,
    );
    return data;
  } catch (err) {
    throw new Error(extractApiError(err, 'Failed to run test download'));
  }
}

function extractApiError(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { error?: string } | undefined;
    if (data?.error) return data.error;
    return err.message || fallback;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}
